'use client'

import { ImportDataModal } from '@/components/import/ImportDataModal'
import PaginationControls from '@/components/pagination/PaginationControls'
import { Partner } from '@/lib/api/common/interfaces/partner.interface'
import {
    getPartners,
    getPartnersByCompany,
    togglePartnerActive,
} from '@/lib/api/partners/partners-api'
import { ShareableModule } from '@/lib/api/share/share-api'
import { useDebounce } from '@/lib/hooks/use-debounce'
import { usePaginatedPage } from '@/lib/hooks/use-paginated-page'
import { useResourcePermissions } from '@/lib/hooks/use-resource-permissions'
import { useUrlPage } from '@/lib/hooks/use-url-page'
import { useAuth } from '@/store/hooks'
import { useCallback, useMemo, useState } from 'react'
import {
    MdAdd,
    MdArrowForward,
    MdBusiness,
    MdClose,
    MdEdit,
    MdEmail,
    MdFileDownload,
    MdPerson,
    MdPhone,
    MdSearch,
    MdToggleOff,
    MdToggleOn,
    MdVisibility,
} from 'react-icons/md'
import { useNavigate } from 'react-router-dom'
import { toast } from 'react-toastify'

export default function PartnersListPage() {
    const { user } = useAuth()
    const navigate = useNavigate()
    const { canCreate, canUpdate } = useResourcePermissions(
        'person',
        'partners'
    )
    const [searchTerm, setSearchTerm] = useState('')
    const [showImportModal, setShowImportModal] = useState(false)
    const [togglingId, setTogglingId] = useState<number | null>(null)
    const [urlPage, setUrlPage] = useUrlPage()

    const debouncedSearch = useDebounce(searchTerm, 300)

    const filters = useMemo(
        () => ({
            companyId: user?.companyId ?? undefined,
            search: debouncedSearch.trim() || undefined,
        }),
        [user?.companyId, debouncedSearch]
    )

    const fetchFn = useCallback(
        (params: typeof filters & { page: number; limit: number }) => {
            return getPartners(params)
        },
        []
    )

    const {
        items: partners,
        loading,
        page,
        limit,
        total,
        setPage,
        reload,
    } = usePaginatedPage<Partner, typeof filters>({
        fetchFn,
        filters,
        limit: 15,
        enabled: !!user?.companyId,
        page: urlPage,
        onPageChange: setUrlPage,
    })

    const handleSearch = (e: React.FormEvent) => {
        e.preventDefault()
    }

    const handleToggleActive = async (partner: Partner) => {
        setTogglingId(partner.id)
        try {
            const res = await togglePartnerActive(partner.id)
            if (res.success) {
                toast.success(
                    partner.active
                        ? 'Parceiro desativado'
                        : 'Parceiro reativado'
                )
                reload()
            } else {
                toast.error(res.error || 'Erro ao alterar o status do parceiro')
            }
        } catch (e: any) {
            toast.error(e?.message || 'Erro ao alterar o status do parceiro')
        } finally {
            setTogglingId(null)
        }
    }

    if (loading && partners.length === 0) {
        return (
            <div className="flex flex-col items-center justify-center min-h-[400px] gap-4 animate-fade-in">
                <div className="loading loading-spinner loading-lg text-secondary"></div>
                <p className="text-base-content/60 text-sm">
                    Carregando parceiros...
                </p>
            </div>
        )
    }

    return (
        <div className="flex flex-col gap-6 animate-fade-in">
            <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-secondary to-secondary/70 flex items-center justify-center shadow-lg">
                        <MdBusiness className="text-2xl text-white" />
                    </div>
                    <div>
                        <h1 className="text-2xl font-bold text-base-content">
                            Parceiros
                        </h1>
                        <p className="text-sm text-base-content/60">
                            {total} {total === 1 ? 'parceiro' : 'parceiros'}{' '}
                            cadastrados
                        </p>
                    </div>
                </div>
                {canCreate && (
                    <div className="flex items-center gap-2">
                        <button
                            className="btn btn-outline btn-secondary gap-2"
                            onClick={() => setShowImportModal(true)}
                        >
                            <MdFileDownload className="text-xl" />
                            <span className="hidden sm:inline">Importar</span>
                        </button>
                        <button
                            className="btn btn-secondary gap-2"
                            onClick={() => navigate('/person/partners/new')}
                        >
                            <MdAdd className="text-xl" />
                            <span className="hidden sm:inline">Adicionar</span>
                        </button>
                    </div>
                )}
            </div>

            <div className="bg-base-100 rounded-xl border border-base-300/50 shadow-sm p-4">
                <form
                    onSubmit={handleSearch}
                    className="flex items-center gap-2"
                >
                    <div className="flex-1 relative">
                        <MdSearch className="absolute z-10 left-3 top-1/2 -translate-y-1/2 text-base-content/70 text-xl pointer-events-none z-10" />
                        <input
                            type="text"
                            placeholder="Buscar por nome, email, telefone, CPF, CNPJ ou endereço..."
                            className="input input-bordered w-full pl-10"
                            value={searchTerm}
                            onChange={e => setSearchTerm(e.target.value)}
                        />
                    </div>
                    {searchTerm && (
                        <button
                            type="button"
                            onClick={() => setSearchTerm('')}
                            className="btn btn-ghost btn-sm"
                        >
                            <MdClose className="text-lg" />
                        </button>
                    )}
                </form>
            </div>

            {partners.length === 0 && !loading ? (
                <div className="flex flex-col items-center justify-center min-h-[400px] gap-4 bg-base-100 rounded-xl border border-base-300/50 p-8">
                    <div className="w-20 h-20 rounded-full bg-base-200 flex items-center justify-center">
                        <MdBusiness className="text-4xl text-base-content/30" />
                    </div>
                    <div className="text-center">
                        <h3 className="text-lg font-semibold text-base-content mb-1">
                            {searchTerm
                                ? 'Nenhum parceiro encontrado'
                                : 'Nenhum parceiro cadastrado'}
                        </h3>
                        <p className="text-sm text-base-content/60 mb-4">
                            {searchTerm
                                ? 'Tente ajustar sua busca'
                                : 'Comece adicionando parceiros para que eles apareçam aqui.'}
                        </p>
                        {!searchTerm && canCreate && (
                            <button
                                onClick={() => navigate('/person/partners/new')}
                                className="btn btn-secondary gap-2"
                            >
                                <MdAdd className="text-xl" />
                                Adicionar primeiro parceiro
                            </button>
                        )}
                    </div>
                </div>
            ) : (
                <div className="flex flex-col gap-4">
                    {partners.map(partner => (
                        <div
                            key={partner.id}
                            onClick={() =>
                                navigate(`/person/partners/${partner.id}`)
                            }
                            className={`group cursor-pointer bg-gradient-to-br from-base-100 to-base-200/30 rounded-xl border border-base-300/50 overflow-hidden hover:shadow-xl hover:-translate-y-1 transition-all duration-300 ${
                                partner.active ? '' : 'opacity-50'
                            }`}
                        >
                            <div className="flex items-start gap-4 p-4">
                                <div className="relative w-16 h-16 flex-shrink-0 bg-gradient-to-br from-secondary/20 to-secondary/10 rounded-xl flex items-center justify-center border border-secondary/20">
                                    {partner.personType === 'JURIDICA' ? (
                                        <MdBusiness className="text-3xl text-secondary" />
                                    ) : (
                                        <MdPerson className="text-3xl text-secondary" />
                                    )}
                                </div>

                                <div className="flex-1 min-w-0">
                                    <div className="flex items-start justify-between gap-2 mb-2">
                                        <div>
                                            <h3 className="text-base font-bold text-base-content line-clamp-1 group-hover:text-secondary transition-colors">
                                                {partner.name}
                                            </h3>
                                            {partner.tradingName && (
                                                <p className="text-sm text-base-content/60">
                                                    {partner.tradingName}
                                                </p>
                                            )}
                                        </div>
                                        {canUpdate && (
                                            <div className="flex gap-2 flex-shrink-0">
                                                <button
                                                    onClick={e => {
                                                        e.stopPropagation()
                                                        handleToggleActive(
                                                            partner
                                                        )
                                                    }}
                                                    disabled={
                                                        togglingId ===
                                                        partner.id
                                                    }
                                                    className={`btn btn-circle btn-sm btn-ghost ${
                                                        partner.active
                                                            ? 'text-success'
                                                            : 'text-base-content/40'
                                                    }`}
                                                    title={
                                                        partner.active
                                                            ? 'Desativar parceiro'
                                                            : 'Reativar parceiro'
                                                    }
                                                >
                                                    {togglingId ===
                                                    partner.id ? (
                                                        <span className="loading loading-spinner loading-xs" />
                                                    ) : partner.active ? (
                                                        <MdToggleOn className="text-xl" />
                                                    ) : (
                                                        <MdToggleOff className="text-xl" />
                                                    )}
                                                </button>
                                                <button
                                                    onClick={e => {
                                                        e.stopPropagation()
                                                        navigate(
                                                            `/person/partners/${partner.id}`
                                                        )
                                                    }}
                                                    className="btn btn-circle btn-sm btn-ghost"
                                                    title="Ver detalhes"
                                                >
                                                    <MdVisibility className="text-lg" />
                                                </button>
                                                <button
                                                    onClick={e => {
                                                        e.stopPropagation()
                                                        navigate(
                                                            `/person/partners/edit/${partner.id}`
                                                        )
                                                    }}
                                                    className="btn btn-circle btn-sm btn-ghost"
                                                    title="Editar parceiro"
                                                >
                                                    <MdEdit className="text-lg" />
                                                </button>
                                            </div>
                                        )}
                                    </div>

                                    <div className="space-y-2 mb-3">
                                        {partner.email && (
                                            <div className="flex items-center gap-2 text-sm">
                                                <MdEmail className="text-base-content/50" />
                                                <span className="text-base-content/80">
                                                    {partner.email}
                                                </span>
                                            </div>
                                        )}
                                        {partner.phone && (
                                            <div className="flex items-center gap-2 text-sm">
                                                <MdPhone className="text-base-content/50" />
                                                <a
                                                    href={`tel:${partner.phone}`}
                                                    onClick={e =>
                                                        e.stopPropagation()
                                                    }
                                                    className="text-secondary hover:underline"
                                                >
                                                    {partner.phone}
                                                </a>
                                            </div>
                                        )}
                                    </div>

                                    <div className="flex items-center justify-between pt-3 border-t border-base-300/50">
                                        <div className="flex items-center gap-2">
                                            <span
                                                className={`badge ${
                                                    partner.active
                                                        ? 'badge-success'
                                                        : 'badge-error'
                                                } text-white`}
                                            >
                                                {partner.active
                                                    ? 'Ativo'
                                                    : 'Inativo'}
                                            </span>
                                            <span className="badge badge-outline">
                                                {partner.personType ===
                                                'JURIDICA'
                                                    ? 'Pessoa Jurídica'
                                                    : 'Pessoa Física'}
                                            </span>
                                            {partner.personType ===
                                                'JURIDICA' &&
                                                partner.cnpj && (
                                                    <span className="badge badge-ghost text-xs">
                                                        CNPJ: {partner.cnpj}
                                                    </span>
                                                )}
                                            {partner.personType === 'FISICA' &&
                                                partner.cpf && (
                                                    <span className="badge badge-ghost text-xs">
                                                        CPF: {partner.cpf}
                                                    </span>
                                                )}
                                        </div>

                                        <MdArrowForward className="text-xl text-base-content/30 group-hover:text-secondary group-hover:translate-x-1 transition-all" />
                                    </div>
                                </div>
                            </div>
                        </div>
                    ))}
                    {total > limit && (
                        <PaginationControls
                            page={page}
                            limit={limit}
                            total={total}
                            loading={loading}
                            onPageChange={setPage}
                            label="parceiro"
                        />
                    )}
                </div>
            )}

            <ImportDataModal
                isOpen={showImportModal}
                onClose={() => setShowImportModal(false)}
                module={ShareableModule.PARTNER}
                moduleLabel="Parceiros"
                fetchItems={async (companyId: number) => {
                    const response = await getPartnersByCompany(companyId)
                    return response.success ? response.result : []
                }}
                renderItem={(item: Partner) => (
                    <div>
                        <p className="text-sm font-medium truncate">
                            {item.name}
                        </p>
                        <p className="text-xs text-base-content/60 truncate">
                            {[item.email, item.phone]
                                .filter(Boolean)
                                .join(' - ') || 'Sem contato'}
                        </p>
                    </div>
                )}
                getItemId={(item: Partner) => item.id}
                getItemName={(item: Partner) => item.name}
                onImportComplete={reload}
            />
        </div>
    )
}
